import {Dimension} from "./dimensions";
import {Metric} from "./metrics";
import {MetricComponentObject, MetricComponentObjects} from '../classes/metric-component-object';
import {DimensionComponentObjects} from '../classes/dimension-component-object';
import {Channel} from './channel';
import {Serializer} from './serializer';
import {ReportChartOptions} from '../classes/chart-report';
import {ColumnState} from '@ag-grid-community/core';

export type AdRPeriodType =
  'none' |
  'today' |
  'yesterday' |
  'this_week' |
  'last_7_days' |
  'last_week' |
  'last_14_days' |
  'this_month' |
  'last_30_days' |
  'last_month' |
  'this_year' |
  'previous_period' |
  'last_year' |
  'custom';

export const FilterTypeApiEnums: any = {
  equals: 'equals',
  notEqual: 'not_equals',
  lessThan: 'lower_than',
  lessThanOrEqual: 'lower_than_equals',
  greaterThan: 'greater_than',
  greaterThanOrEqual: 'greater_than_equals',
  inRange: 'between',
  contains: 'contains',
  notContains: 'not_contains',
  startsWith: 'begins_with',
  endsWith: 'ends_with'
}

export const FilterTypeAppEnums: any = {
  equals: 'equals',
  not_equals: 'notEqual',
  lower_than: 'lessThan',
  lower_than_equals: 'lessThanOrEqual',
  greater_than: 'greaterThan',
  greater_than_equals: 'greaterThanOrEqual',
  between: 'inRange',
  contains: 'contains',
  not_contains: 'notContains',
  begins_with: 'startsWith',
  ends_with: 'endsWith',
  inRange: 'inRange'
}

export interface AdRReportSize {
  all:    number;
  page:   number;
}

export interface AdRReport {
  rows:       AdRResponseRows;
  parameters: AdRResponseParameters;
  totals:     AdRResponseTotals;
  size:       AdRReportSize
}

export type AdRReportType =           'performance' | 'cycle' | 'graph' | 'chart' | 'dashboard' | 'affinity' | 'attribution-overview' | 'transaction-id' | 'path-explorer';
export type AdRReportColumnAction =   'value' | 'comparison' | 'percentage';
export type AdRReportShareOption =     'private' | 'space' | 'public';
export type AdRReportParameter =        'dimensions' | 'metrics' | 'conversion_metric' | 'channel' | 'period' | 'filters';

export type AdRComparisonCompare = 'period' | 'model';

export interface AdRComparison {
  on:        AdRComparisonCompare;
  value:     any;
}

export interface AdRPeriod {
  type:          AdRPeriodType,
  from?:         string;
  to?:           string;
  compareWith?:  AdRComparison;
  name?:         string;
}

// All available parameters
export interface AdRequestParams {
  conversion_metric:    string;
  dimensions:           Array<string>;
  metrics:              Array<string>;
  period:               AdRPeriod;
  compare:              AdRComparison;
  filters:              any;
  dimension_filters:    AdRParamsFilters;
  metric_filters:       AdRParamsFilters;
  dimension_orders:     AdRParamsOrders;
  metric_orders:        AdRParamsOrders;
  consent_correction:   boolean;
  limit:                number;
  page:                 number;

  // TODO à typer
  channel:              string;
  dashboard:            Array<any>;
  column_state:         Array<ColumnState>;

  keep_single_events:   boolean;
  before_events:        number;
  after_events:         number;

  groupingBy: 'day' | 'month' | 'week' | 'year';
  options: ReportChartOptions;
  tableColumnState: any;
  tableFilterModel: any;
  report_id: number | null;
  template_id: number | null;
}

export interface AdReportParams {
  conversion_metric:    MetricComponentObject;
  dimensions:           DimensionComponentObjects;
  metrics:              MetricComponentObjects;
  channel:              Serializer<Channel>;
  period:               any;
  compare:              any;
  filters:              any;
  dashboard:            any;
}

// Performance

export interface AdRequestParamsPerformance extends Partial<AdRequestParams> {
  dimensions:           Array<string>;
  metrics:              Array<string>;
  period:               AdRPeriod;
  compare:              AdRComparison;
  filters:              AdRParamsDimensionFilters;
  dimension_filters:    AdRParamsFilters;
  metric_filters:       AdRParamsFilters;
  dimension_orders:     AdRParamsOrders;
  metric_orders:        AdRParamsOrders;
  limit:                number;
  page:                 number;
}

export interface AdReportParamsPerformance extends Partial<AdReportParams> {
  dimensions:           DimensionComponentObjects;
  metrics:              MetricComponentObjects;
  channel:              Serializer<Channel>;
  period:               any;
  compare:              any;
  filters:              any;
}

// Cycle

export interface AdRParamsCycle extends Partial<AdRequestParams> {
  dimensions:           Array<string>;
  metrics:              Array<string>;
  conversion_metric:    string;
  period:               AdRPeriod;
  compare:              AdRComparison;
  filters:              AdRParamsDimensionFilters;
  dimension_filters:    AdRParamsFilters;
  metric_filters:       AdRParamsFilters;
  dimension_orders:     AdRParamsOrders;
  metric_orders:        AdRParamsOrders;
  limit:                number;
  page:                 number;
}

// Path explorer

export interface AdRequestParamsPathExplorer extends Partial<AdRequestParams> {
  conversion_metric: string,
  period: AdRPeriod,
  channel: string,
  filter: any
}

export interface AdReportParamsPathExplorer extends Partial<AdReportParams> {
  conversion_metric: MetricComponentObject,
  channel: Serializer<Channel>,
  period: any,
  filter: any
}

// Channel Affinity

export interface AdRequestParamsChannelAffinity extends Partial<AdRequestParams> {
  conversion_metric:  string;
  channel:            string;
  period:             AdRPeriod;
  keep_single_events: boolean;
  before_events:      number;
  after_events:       number;
}

export interface AdReportParamsChannelAffinity extends Partial<AdReportParams> {
  conversion_metric:  MetricComponentObject;
  channel:            Serializer<Channel>;
  period:             any;
  keep_single_events: boolean;
  before_events:      number;
  after_events:       number;
}

// Chart report

export interface AdRequestParamsChart extends Partial<AdRequestParams> {}

export interface AdReportParamsChart extends Partial<AdReportParams> {}

export interface AdRequestOptions {
  generate_missing_date_chart_data: boolean;
  round_big_numbers: boolean;
  real_conversions: boolean;
}

// Attribution overview
//{ conversion_metric: any, channel: any, period: any }

export interface AdRequestParamsAttributionOverview extends Partial<AdRequestParams> {
  conversion_metric: string;
  channel: string;
  period: AdRPeriod;
}

export interface AdReportParamsAttributionOverview extends Partial<AdReportParams> {
  conversion_metric: MetricComponentObject;
  channel: Serializer<Channel>;
  period: AdRPeriod;
}

export interface AdRParamsDimensionFilters {
  [key: string]: Array<string>
}

export type AdRParamsFilterOperator = 'OR' | 'AND';

export interface AdRParamsFilter {
  slug:          string;
  action:        AdRReportColumnAction;
  between:       boolean;
  operator:      AdRParamsFilterOperator;
  condition1:    Partial<AdRParamsFilter>;
  condition2:    Partial<AdRParamsFilter>;
  value:         any;
  filter:        number;
  filterTo:      number;
  filterType:    string;
  type:          string;
}

export type AdRParamsFilters = Array<AdRParamsFilter>;

export type AdRParamsOrderSort = 'asc' | 'desc';

export interface AdRParamsOrder {
  sort:   AdRParamsOrderSort;
  slug:   string;
  action: AdRReportColumnAction;
}

export type AdRParamsOrders = Array<AdRParamsOrder>;

export interface AdRResponse {
  rows:       AdRResponseRows;
  parameters: AdRResponseParameters;
  totals:     AdRResponseTotals;
}

export type AdRResponseRows = Array<AdRResponseRow>;

export interface AdRResponseRow {
  dimensions:   AdRResponseRowValues;
  metrics:      AdRResponseRowValues;
}

export type AdRResponseRowValues = { [key: string]: AdRResponseRowValue };

export interface AdRResponseRowValue {
  metric?: Metric;
  dimension?: Dimension;
  origin: string;
  value: any;
  formatted_value: any;
  name: string;
}

export interface AdRResponseParameters {
  columns:        any;
  currency:       string;
  data_set:       string;
  end_date:       string;
  limit:          number;
  page:           number;
  sql_query:      string;
  start_date:     string;
  timezone:       string;
  number_format:  string;
  timings: {
    prepared_in:  string;
    presented_in: string;
    executed_in:  string;
  }
}

export interface AdRResponseTotals {
  all:  AdRResponseTotalsValues;
  page: AdRResponseTotalsValues;
}

export type AdRResponseTotalsValues = { [key: string]: any };

export interface ReportTemplate {
  category: string;
  icon: string;
  name: string;
  parameters: Partial<AdRequestParams>;
  options: {
    type: AdRReportType
  }
}

export type Lang = 'en' | 'fr';

export interface DimensionFilter {
  dimension_id: string,
  values: Array<number>
}

export interface Filters {
  data: null | Array<DimensionFilter>;
  rows: null
}
